import { CSSTransition } from 'react-transition-group';

import { Icon as BlueIcon } from '@blueprintjs/core';
import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';
import { Image } from '@mantine/core';

import { State, useState } from '@hookstate/core';
import { getUserMembers } from '@services/queries/studios';
import { useQuery } from 'react-query';
import { useLocalStorage } from '@hooks';

import { ChangeAssigneeLoading } from '@pages/Asset/components/Sidebar/components/ChangeAssignee/ChangeAssignee.loading';
import {
  Block,
  Avatar,
  Close,
  Divider,
  Header,
  Icon,
  InputContainer,
  InputSearch,
  Item,
  Items,
  Label,
  SearchIconContainer,
  Title,
  Remove,
  ItemNoFeedback,
  UsersSeparator,
  SearchCloseIconContainer,
} from '@pages/Asset/components/Sidebar/components/ChangeAssignee/ChangeAssignee.styles';
import { IUserMember } from '@services/queries/studios/getUserMembers/getUserMembers';
import { NeededUserInfo } from './types';

interface IRecentUser {
  id: string;
  date: Date;
}

interface IChangeAssignee {
  open: State<boolean>;
  actualUser: NeededUserInfo | undefined | null;
  handleUserChange: (IUser: Pick<IUserMember, 'name' | 'id'> | null) => void;
}

export const ChangeAssignee = ({
  open,
  actualUser,
  handleUserChange,
}: IChangeAssignee) => {
  const [recentUsers, setRecentUsers] = useLocalStorage<IRecentUser[]>(
    'recentAssigneeUsers',
    [],
  );

  const searchLocal = useState<string>('');
  const searchUser = useState<string>('');

  // const mutation = useMutation(patchEquipment, {
  //   onSuccess: (_, { data: { userId } }) => {
  //     open.set(false);
  //
  //     if (userId) {
  //       setRecentUsers(
  //         [
  //           { id: userId, date: new Date() },
  //           ...recentUsers.filter(item => item.id !== userId),
  //         ].slice(0, 5),
  //       );
  //     }
  //   },
  // });

  const { data, isLoading, isError } = useQuery('members', getUserMembers);

  const findCurrentUser = data?.find(item => item.id === actualUser?.id);

  return (
    <CSSTransition
      in={open.get()}
      timeout={300}
      classNames="my-node"
      unmountOnExit
      onEnter={() => open.set(true)}
      onExited={() => open.set(false)}
    >
      <Block className="massEdit">
        <Header>
          <Title>{actualUser ? 'Change Assignee' : 'Assign to'}</Title>
          <Close onClick={() => open.set(false)}>
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </Header>
        <Divider />

        <InputContainer>
          <SearchIconContainer>
            <BlueIcon icon="search" color="#8F95B2" size={12} />
          </SearchIconContainer>
          <InputSearch
            placeholder="User name or Email"
            value={searchLocal.get()}
            onChange={e => searchLocal.set(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                searchUser.set(searchLocal.get());
              }
            }}
          />
          {searchUser.get() && (
            <SearchCloseIconContainer
              onClick={() => {
                searchUser.set('');
                searchLocal.set('');
              }}
            >
              <BlueIcon icon="small-cross" color="#8F95B2" />
            </SearchCloseIconContainer>
          )}
        </InputContainer>

        {isLoading && <ChangeAssigneeLoading />}

        {!isLoading && (
          <Items className="bscroll">
            {findCurrentUser && (
              <>
                <Label className="h1">Current Assignee</Label>
                <ItemNoFeedback>
                  <Icon>
                    <Image
                      src={findCurrentUser.picture || AvatarDefaultImg}
                      alt=""
                      radius={12}
                      width="24px"
                      height="24px"
                      withPlaceholder
                      placeholder={
                        <Avatar
                          className="defaultImage"
                          src={AvatarDefaultImg}
                          alt=""
                        />
                      }
                    />
                  </Icon>
                  <Label>{findCurrentUser.name}</Label>
                  <Remove
                    type="button"
                    onClick={() => {
                      handleUserChange(null);
                      open.set(false);
                    }}
                  >
                    <BlueIcon icon="cross" color="#D14343" size={12} />
                  </Remove>
                </ItemNoFeedback>
              </>
            )}

            {!isLoading &&
              !isError &&
              data &&
              (() => {
                const filteredUsers = data
                  .filter(item => item.id !== actualUser?.id)
                  .filter(item =>
                    searchUser.get()
                      ? item.name
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase()) ||
                        item.email
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase())
                      : true,
                  );

                const showRecent =
                  filteredUsers.filter(
                    item =>
                      !!recentUsers.find(userItem => userItem.id === item.id),
                  ).length > 0;

                const showNoRecent =
                  filteredUsers.filter(
                    item =>
                      !recentUsers.find(userItem => userItem.id === item.id),
                  ).length > 0;

                const showSeparator = showRecent && showNoRecent;

                return (
                  <>
                    {findCurrentUser && (showRecent || showNoRecent) && (
                      <UsersSeparator />
                    )}
                    {showRecent && <Label className="h1">Recent</Label>}
                    {recentUsers
                      .map(item =>
                        filteredUsers.find(userItem => userItem.id === item.id),
                      )
                      .filter(
                        item =>
                          !!recentUsers.find(
                            userItem => userItem.id === item?.id,
                          ),
                      )
                      .map(item => (
                        <Item
                          key={item?.id}
                          onClick={() => {
                            handleUserChange(item ?? null);
                            open.set(false);
                            if (item) {
                              setRecentUsers(
                                [
                                  { id: item.id, date: new Date() },
                                  ...recentUsers.filter(
                                    itemFilter => itemFilter.id !== item.id,
                                  ),
                                ].slice(0, 5),
                              );
                            }
                          }}
                        >
                          <Icon>
                            <Image
                              src={item?.picture || AvatarDefaultImg}
                              alt=""
                              width="24px"
                              height="24px"
                              radius={12}
                              placeholder={
                                <Avatar
                                  className="defaultImage"
                                  src={AvatarDefaultImg}
                                  alt=""
                                />
                              }
                            />
                          </Icon>
                          <Label>{item?.name}</Label>
                        </Item>
                      ))}

                    {showRecent && showSeparator && <UsersSeparator />}

                    {filteredUsers
                      .filter(
                        item =>
                          !recentUsers.find(
                            userItem => userItem.id === item.id,
                          ),
                      )
                      .map(item => (
                        <Item
                          key={item.id}
                          onClick={() => {
                            handleUserChange(item);
                            if (item) {
                              setRecentUsers(
                                [
                                  { id: item.id, date: new Date() },
                                  ...recentUsers.filter(
                                    itemFilter => itemFilter.id !== item.id,
                                  ),
                                ].slice(0, 5),
                              );
                            }
                            open.set(false);
                          }}
                        >
                          <Icon>
                            <Image
                              src={item.picture || AvatarDefaultImg}
                              alt=""
                              width="24px"
                              height="24px"
                              radius={12}
                              placeholder={
                                <Avatar
                                  className="defaultImage"
                                  src={AvatarDefaultImg}
                                  alt=""
                                />
                              }
                            />
                          </Icon>
                          <Label>{item.name}</Label>
                        </Item>
                      ))}
                  </>
                );
              })()}
          </Items>
        )}
      </Block>
    </CSSTransition>
  );
};
